import React, { memo, useEffect, useRef, useState } from "react";

const DST_HEADER_SIZE = 512;
const THREE_SCRIPT_URL = "https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js";

const STITCH_COMMAND = {
    Normal: 0,
    Jump: 1,
    Trim: 2,
    Stop: 4,
    End: 8,
};

const scriptPromises = {};

// Tải script ngoài một lần duy nhất để renderer dùng đúng Three.js giống viewer HTML mẫu.
function loadScriptOnce(src) {
    if (window.THREE && src == THREE_SCRIPT_URL) return Promise.resolve();

    if (scriptPromises[src]) return scriptPromises[src];
    scriptPromises[src] = new Promise((resolve, reject) => {
        const existed = Array.from(document.scripts).find((script) => script.src == src);
        if (existed) {
            existed.addEventListener("load", resolve, { once: true });
            existed.addEventListener("error", reject, { once: true });
            setTimeout(() => {
                if (window.THREE) resolve();
            }, 0);
            return;
        }
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
    return scriptPromises[src];
}

// Đảm bảo Three.js đã sẵn sàng trước khi dựng tube geometry.
async function ensureThree() {
    if (window.THREE) return window.THREE;
    await loadScriptOnce(THREE_SCRIPT_URL);
    return window.THREE;
}

// Đọc màu hex đơn giản, fallback về đen để preview DST luôn render được.
function normalizeThreadColor(color) {
    return /^#[0-9a-fA-F]{6}$/.test(color || "") ? color : "#111111";
}

// Convert màu hex sang object giống Color của viewer.
function hexToThreadColor(color) {
    const hex = normalizeThreadColor(color).replace("#", "");
    return {
        r: parseInt(hex.substring(0, 2), 16),
        g: parseInt(hex.substring(2, 4), 16),
        b: parseInt(hex.substring(4, 6), 16),
    };
}

// Decode lệnh DST từ byte thứ 3 theo đúng bit flags của dstformat.js trong viewer.
function decodeCommand(b2) {
    let result = STITCH_COMMAND.Normal;
    if (b2 === 0xf3) return STITCH_COMMAND.End;
    if ((b2 & 0xc3) === 0xc3) return STITCH_COMMAND.Trim | STITCH_COMMAND.Stop;
    if (b2 & 0x80) result |= STITCH_COMMAND.Trim;
    if (b2 & 0x40) result |= STITCH_COMMAND.Stop;
    return result;
}

// Decode trục X của một record DST theo bảng bit chuẩn Tajima.
function decodeDx(b0, b1, b2) {
    let x = 0;
    if (b0 & 0x01) x += 1;
    if (b0 & 0x02) x -= 1;
    if (b0 & 0x04) x += 9;
    if (b0 & 0x08) x -= 9;
    if (b1 & 0x01) x += 3;
    if (b1 & 0x02) x -= 3;
    if (b1 & 0x04) x += 27;
    if (b1 & 0x08) x -= 27;
    if (b2 & 0x04) x += 81;
    if (b2 & 0x08) x -= 81;
    return x;
}

// Decode trục Y của một record DST; viewer sẽ invert vertical qua các bước Pattern.
function decodeDy(b0, b1, b2) {
    let y = 0;
    if (b0 & 0x80) y += 1;
    if (b0 & 0x40) y -= 1;
    if (b0 & 0x20) y += 9;
    if (b0 & 0x10) y -= 9;
    if (b1 & 0x80) y += 3;
    if (b1 & 0x40) y -= 3;
    if (b1 & 0x20) y += 27;
    if (b1 & 0x10) y -= 27;
    if (b2 & 0x20) y += 81;
    if (b2 & 0x10) y -= 81;
    return y;
}

// Parse DST thành stitch tuyệt đối theo flow dstRead của viewer.
function parseDst(arrayBuffer) {
    const bytes = new Uint8Array(arrayBuffer || []);
    const stitches = [];
    let x = 0;
    let y = 0;
    for (let pos = DST_HEADER_SIZE; pos + 2 < bytes.length; pos += 3) {
        const b0 = bytes[pos];
        const b1 = bytes[pos + 1];
        const b2 = bytes[pos + 2];
        const command = decodeCommand(b2);
        if (command === STITCH_COMMAND.End) break;
        x += decodeDx(b0, b1, b2);
        y += decodeDy(b0, b1, b2);
        stitches.push({ x, y, z: 0, command });
    }
    invertPatternVertical(stitches);
    moveToPositive(stitches);
    return stitches;
}

// Tính bounds giống calculateBoundingBox của viewer: bỏ các điểm trim khi lấy vùng vẽ.
function calculateBounds(stitches) {
    const drawable = stitches.filter((stitch) => !(stitch.command & STITCH_COMMAND.Trim));
    if (!drawable.length) return { left: 0, top: 0, right: 1, bottom: 1 };
    return drawable.reduce(
        (result, stitch) => ({
            left: Math.min(result.left, stitch.x),
            top: Math.min(result.top, stitch.y),
            right: Math.max(result.right, stitch.x),
            bottom: Math.max(result.bottom, stitch.y),
        }),
        { left: 99999, top: 99999, right: -99999, bottom: -99999 }
    );
}

// Invert trục Y giống Pattern.prototype.invertPatternVertical.
function invertPatternVertical(stitches) {
    stitches.forEach((stitch) => {
        stitch.y = -stitch.y;
    });
}

// Dời pattern về tọa độ dương giống viewer gọi moveToPositive sau khi đọc file.
function moveToPositive(stitches) {
    const bounds = calculateBounds(stitches);
    stitches.forEach((stitch) => {
        stitch.x -= bounds.left;
        stitch.y -= bounds.top;
    });
}

// Dời pattern về tâm trước khi dựng 3D giống Pattern.prototype.moveToCenter.
function moveToCenter(stitches) {
    const bounds = calculateBounds(stitches);
    const centerX = (bounds.right + bounds.left) / 2;
    const centerY = (bounds.bottom + bounds.top) / 2;
    stitches.forEach((stitch) => {
        stitch.x -= centerX;
        stitch.y -= centerY;
    });
}

// Tính khoảng cách bình phương để xử lý giao nhau giống pattern.js.
function distanceSquared(a, b) {
    return Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2);
}

// Tìm giao điểm giữa hai đoạn stitch để đẩy chỉ nằm trên lên z cao hơn.
function lineIntersection(p1, p2, p3, p4) {
    const result = { x: null, y: null, intersects: false };
    const denominator = (p4.y - p3.y) * (p2.x - p1.x) - (p4.x - p3.x) * (p2.y - p1.y);
    if (denominator === 0) return result;
    const dy1 = p1.y - p3.y;
    const dx1 = p1.x - p3.x;
    const a = ((p4.x - p3.x) * dy1 - (p4.y - p3.y) * dx1) / denominator;
    const b = ((p2.x - p1.x) * dy1 - (p2.y - p1.y) * dx1) / denominator;
    result.x = p1.x + a * (p2.x - p1.x);
    result.y = p1.y + a * (p2.y - p1.y);
    result.intersects = a > 0 && a < 1 && b > 0 && b < 1;
    return result;
}

// Gán z cho stitch theo cùng ý tưởng removeUnderlayment của viewer để tube 3D có độ chồng chỉ.
function applyStitchZ(stitches, zStep = 1) {
    if (!stitches.length) return;
    stitches[0].z = zStep;
    for (let i = 1; i < stitches.length; i += 1) {
        const prev = stitches[i - 1];
        const current = stitches[i];
        current.z = zStep;

        const crossed = [];
        for (let j = 1; j < i - 1; j += 1) {
            const crossing = lineIntersection(prev, current, stitches[j - 1], stitches[j]);
            if (!crossing.intersects) continue;
            const segmentLength = distanceSquared(stitches[j], stitches[j - 1]);
            if (segmentLength === 0) continue;
            const ratio = distanceSquared(stitches[j - 1], crossing) / segmentLength;
            crossed.push({
                x: crossing.x,
                y: crossing.y,
                z: ratio * (stitches[j].z - stitches[j - 1].z) + 2 * zStep + stitches[j - 1].z,
                command: current.command,
                distance: distanceSquared(crossing, current),
            });
        }
        crossed.sort((a, b) => b.distance - a.distance);
        let lastZ = current.z;
        crossed.forEach((crossStitch) => {
            if (lastZ < crossStitch.z) {
                stitches.splice(i, 0, crossStitch);
                lastZ = crossStitch.z;
                i += 1;
            }
        });
    }
    for (let i = 1; i < stitches.length; i += 1) {
        const prev = stitches[i - 1];
        const current = stitches[i];
        if (Math.abs(prev.x - current.x) > 128 || Math.abs(prev.y - current.y) > 128) {
            current.command = STITCH_COMMAND.Trim;
        }
    }
}

// Tạo thread groups giống Pattern.drawShape3d để đưa vào Three TubeGeometry.
function buildThreadGroups(parsedStitches, threadColor) {
    const stitches = parsedStitches.map((stitch) => ({ ...stitch }));
    if (!stitches.length) return [];
    invertPatternVertical(stitches);
    moveToCenter(stitches);
    applyStitchZ(stitches, 1);

    const groups = [{ color: hexToThreadColor(threadColor), points: [] }];
    let currentGroup = groups[0];
    for (let i = 0; i < stitches.length - 1; i += 1) {
        const stitch = stitches[i];
        if (stitch.command === STITCH_COMMAND.Jump || stitch.command === STITCH_COMMAND.Trim || stitch.command === STITCH_COMMAND.Stop) {
            currentGroup = { color: hexToThreadColor(threadColor), points: [] };
            groups.push(currentGroup);
            currentGroup.points.push({ x: stitch.x, y: stitch.y, z: 0 });
        }
        currentGroup.points.push({ x: stitch.x, y: stitch.y, z: stitch.z || 0 });
    }
    return groups.filter((group) => group.points.length > 1);
}

// Tính bounds hiển thị cho 3D từ các mesh có thật, bỏ các đoạn quá dài do trim/jump để chữ không bị thu nhỏ.
function calculateThreadRenderBounds(threadGroups) {
    const points = threadGroups.flatMap((group) => group.points || []);
    if (!points.length) return { left: 0, top: 0, right: 1, bottom: 1 };
    const rawBounds = points.reduce(
        (result, point) => ({
            left: Math.min(result.left, point.x),
            top: Math.min(result.top, point.y),
            right: Math.max(result.right, point.x),
            bottom: Math.max(result.bottom, point.y),
        }),
        { left: 99999, top: 99999, right: -99999, bottom: -99999 }
    );
    const rawWidth = Math.max(1, rawBounds.right - rawBounds.left);
    const rawHeight = Math.max(1, rawBounds.bottom - rawBounds.top);
    const centerX = (rawBounds.left + rawBounds.right) / 2;
    const centerY = (rawBounds.top + rawBounds.bottom) / 2;
    const filteredPoints = points.filter((point) => Math.abs(point.x - centerX) <= rawWidth * 0.48 && Math.abs(point.y - centerY) <= rawHeight * 0.48);
    if (!filteredPoints.length) return rawBounds;
    return filteredPoints.reduce(
        (result, point) => ({
            left: Math.min(result.left, point.x),
            top: Math.min(result.top, point.y),
            right: Math.max(result.right, point.x),
            bottom: Math.max(result.bottom, point.y),
        }),
        { left: 99999, top: 99999, right: -99999, bottom: -99999 }
    );
}

// Vẽ fallback canvas 2D khi Three.js chưa load được, tránh editor bị trắng.
function getFallbackRenderLayout(container, stitches) {
    if (!container || !stitches.length) return null;
    const width = Math.round(container.clientWidth || 0);
    const height = Math.round(container.clientHeight || 0);
    if (!(width > 1) || !(height > 1)) return null;
    const bounds = calculateBounds(stitches);
    const spanX = Math.max(1, bounds.right - bounds.left);
    const spanY = Math.max(1, bounds.bottom - bounds.top);
    const padding = Math.max(2, Math.min(width, height) * 0.08);
    const scale = Math.min((width - padding * 2) / spanX, (height - padding * 2) / spanY);
    return {
        bounds,
        scale,
        offsetX: (width - spanX * scale) / 2,
        offsetY: (height - spanY * scale) / 2,
        renderWidth: spanX * scale,
        width,
        height,
    };
}

function createBaselineStyleFromFallback(container, stitches, baselineMm) {
    const baselineUnits = Number(baselineMm) * 10;
    if (!(baselineUnits > 0)) return null;
    const layout = getFallbackRenderLayout(container, stitches);
    if (!layout) return null;
    return {
        left: layout.offsetX,
        top: layout.offsetY + (baselineUnits - layout.bounds.top) * layout.scale,
        width: layout.renderWidth,
    };
}

function createBaselineStyleFromThreeView(view, baselineMm) {
    const baselineUnits = Number(baselineMm) * 10;
    if (!(baselineUnits > 0) || !view || !(view.fitScale > 0)) return null;
    const sourceBounds = view.sourceBounds || { top: 0, bottom: view.renderHeight || 1 };
    const sourceCenterY = (sourceBounds.top + sourceBounds.bottom) / 2;
    return {
        left: Math.max(0, (view.width - view.renderWidth * view.fitScale) / 2),
        top: view.height / 2 + (baselineUnits - sourceCenterY + view.renderCenterY) * view.fitScale,
        width: Math.max(1, view.renderWidth * view.fitScale),
    };
}

// Vẽ fallback canvas 2D khi Three.js chưa load được, tránh editor bị trắng.
function renderFallbackCanvas(canvas, stitches, threadColor) {
    if (!canvas || !stitches.length) return false;
    canvas.style.display = "block";
    const layout = getFallbackRenderLayout(canvas.parentElement || canvas, stitches);
    if (!layout) return false;
    const { bounds, scale, offsetX, offsetY, width, height } = layout;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, width, height);
    let prev = null;
    ctx.strokeStyle = normalizeThreadColor(threadColor);
    ctx.lineWidth = Math.max(1.2, Math.min(8, scale * 5));
    ctx.lineCap = "round";
    stitches.forEach((stitch) => {
        const point = {
            x: (stitch.x - bounds.left) * scale + offsetX,
            y: (stitch.y - bounds.top) * scale + offsetY,
        };
        if (prev && !(stitch.command & STITCH_COMMAND.Trim) && stitch.command !== STITCH_COMMAND.Stop) {
            ctx.beginPath();
            ctx.moveTo(prev.x, prev.y);
            ctx.lineTo(point.x, point.y);
            ctx.stroke();
        }
        prev = point;
    });
    return true;
}

// Dọn renderer WebGL để tránh leak context khi element trong editor unmount.
function disposeThree(view) {
    if (!view) return;
    if (view.animationFrameId) cancelAnimationFrame(view.animationFrameId);
    if (view.resizeHandler) window.removeEventListener("resize", view.resizeHandler);
    if (view.scene) {
        view.scene.traverse((item) => {
            if (item.geometry) item.geometry.dispose();
            if (item.material) {
                if (Array.isArray(item.material)) item.material.forEach((material) => material.dispose());
                else item.material.dispose();
            }
        });
    }
    if (view.renderer) {
        view.renderer.dispose();
        if (typeof view.renderer.forceContextLoss == "function") view.renderer.forceContextLoss();
        if (view.renderer.domElement && view.renderer.domElement.parentNode) {
            view.renderer.domElement.parentNode.removeChild(view.renderer.domElement);
        }
    }
}

// Fit lại camera/render size trên renderer hiện có, tránh tạo WebGL context mới khi container đổi kích thước.
function resizeThreeView(view, container) {
    if (!view || !view.renderer || !view.camera || !view.root || !container) return false;
    const width = Math.round(container.clientWidth || 0);
    const height = Math.round(container.clientHeight || 0);
    if (!(width > 1) || !(height > 1)) return false;
    view.camera.left = -width / 2;
    view.camera.right = width / 2;
    view.camera.top = height / 2;
    view.camera.bottom = -height / 2;
    view.camera.updateProjectionMatrix();
    view.renderer.setSize(width, height);
    const fitScale = Math.min(width / view.renderWidth, height / view.renderHeight) * 0.9;
    view.root.position.set(-view.renderCenterX * fitScale, -view.renderCenterY * fitScale, 0);
    view.root.scale.set(fitScale, fitScale, fitScale);
    view.fitScale = fitScale;
    view.width = width;
    view.height = height;
    view.render();
    return true;
}

// Đổi màu vật liệu trên mesh hiện có, không rebuild geometry để preview đổi màu nhẹ hơn và không tạo context mới.
function updateThreeColor(view, threadColor) {
    if (!view || !view.materials || !view.materials.length) return;
    const color = hexToThreadColor(threadColor);
    const colorValue = (color.r << 16) | (color.g << 8) | color.b;
    view.materials.forEach((material) => {
        if (material && material.color) material.color.setHex(colorValue);
    });
    if (typeof view.render == "function") view.render();
}

// Render 3D đúng flow viewer: groups -> CatmullRomCurve3 -> TubeGeometry -> MeshPhongMaterial.
function renderThree(container, stitches, threadColor) {
    const THREE = window.THREE;
    if (!container || !THREE || !stitches.length) return null;
    const width = Math.round(container.clientWidth || 0);
    const height = Math.round(container.clientHeight || 0);
    if (!(width > 1) || !(height > 1)) return null;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    renderer.setSize(width, height);
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    renderer.domElement.style.display = "block";
    container.innerHTML = "";
    container.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-width / 2, width / 2, height / 2, -height / 2, 1, 10000);
    camera.position.set(0, 0, 1000);
    const light = new THREE.PointLight(0xffffff);
    light.position.set(100, 100, 250);
    scene.add(light);
    scene.add(new THREE.AmbientLight(0xffffff, 0.22));

    const root = new THREE.Group();
    const materials = [];
    const sourceBounds = calculateBounds(stitches);
    const threadGroups = buildThreadGroups(stitches, threadColor);
    threadGroups.forEach((group, index) => {
        const curvePoints = group.points.map((point) => new THREE.Vector3(point.x, point.y, point.z));
        if (curvePoints.length < 2) return;
        const curve = new THREE.CatmullRomCurve3(curvePoints);
        const geometry = new THREE.TubeGeometry(curve, 4 * curvePoints.length, 2.5, 4, false);
        const color = (group.color.r << 16) | (group.color.g << 8) | group.color.b;
        const material = new THREE.MeshPhongMaterial({ color, specular: 0x050505, shininess: 0 });
        materials.push(material);
        const mesh = new THREE.Mesh(geometry, material);
        mesh.name = `thread${index}`;
        root.add(mesh);
    });
    if (!root.children.length) {
        renderer.dispose();
        if (typeof renderer.forceContextLoss == "function") renderer.forceContextLoss();
        if (renderer.domElement && renderer.domElement.parentNode) {
            renderer.domElement.parentNode.removeChild(renderer.domElement);
        }
        return null;
    }
    scene.add(root);

    const renderBounds = calculateThreadRenderBounds(threadGroups);
    const renderWidth = Math.max(1, renderBounds.right - renderBounds.left);
    const renderHeight = Math.max(1, renderBounds.bottom - renderBounds.top);
    const renderCenterX = (renderBounds.right + renderBounds.left) / 2;
    const renderCenterY = (renderBounds.bottom + renderBounds.top) / 2;
    camera.lookAt(0, 0, 0);

    const view = {
        renderer,
        scene,
        camera,
        root,
        materials,
        renderWidth,
        renderHeight,
        renderCenterX,
        renderCenterY,
        sourceBounds,
        width,
        height,
        fitScale: 1,
        render: () => renderer.render(scene, camera),
    };
    resizeThreeView(view, container);
    return view;
}

// Component preview file DST text thêu bằng Three.js tube geometry giống viewer ngoài.
function EmbroideryDstCanvas3D({ dstUrl, threadColor = "#111111", className = "", style = {}, useWebGL = true, baselineMm = 0, showBaseline = false }) {
    const canvasRef = useRef(null);
    const threeRef = useRef(null);
    const viewRef = useRef(null);
    const stitchesRef = useRef([]);
    const threadColorRef = useRef(threadColor);
    const baselineMmRef = useRef(baselineMm);
    const renderRafRef = useRef(null);
    const webglBuildRafRef = useRef(null);
    const previewVersionRef = useRef(0);
    const rebuildThreeRef = useRef(false);
    const [status, setStatus] = useState("");
    const [baselineStyle, setBaselineStyle] = useState(null);

    const updateBaselineStyle = (nextStyle) => {
        if (!showBaseline || !nextStyle) {
            setBaselineStyle(null);
            return;
        }
        setBaselineStyle({
            left: `${Math.round(nextStyle.left)}px`,
            top: `${Math.round(nextStyle.top)}px`,
            width: `${Math.round(nextStyle.width)}px`,
        });
    };

    // Hiển thị canvas 2D ngay lập tức để người dùng không phải chờ WebGL dựng mesh xong.
    const showFallbackPreview = (stitches) => {
        renderFallbackCanvas(canvasRef.current, stitches, threadColorRef.current);
        updateBaselineStyle(createBaselineStyleFromFallback(canvasRef.current?.parentElement || canvasRef.current, stitches, baselineMmRef.current));
        if (canvasRef.current) canvasRef.current.style.display = "block";
        if (threeRef.current) {
            threeRef.current.style.visibility = "visible";
            threeRef.current.style.display = "none";
        }
    };

    // Hủy lượt dựng WebGL đang chờ để lần đổi size/màu mới nhất luôn thắng.
    const cancelPendingWebGLBuild = () => {
        if (!webglBuildRafRef.current) return;
        cancelAnimationFrame(webglBuildRafRef.current);
        webglBuildRafRef.current = null;
    };

    // Dựng WebGL sau khi canvas 2D đã có cơ hội paint, rồi mới swap sang 3D.
    const scheduleWebGLBuild = (stitches, previewVersion) => {
        cancelPendingWebGLBuild();
        webglBuildRafRef.current = requestAnimationFrame(() => {
            webglBuildRafRef.current = null;
            if (previewVersion != previewVersionRef.current || !rebuildThreeRef.current || !useWebGL) return;
            const oldView = viewRef.current;
            // Bật container 3D ở trạng thái ẩn để lấy được kích thước thật trước khi swap.
            if (threeRef.current) {
                threeRef.current.style.display = "block";
                threeRef.current.style.visibility = "hidden";
            }
            let nextView = null;
            try {
                nextView = renderThree(threeRef.current, stitches, threadColorRef.current);
            } catch {
                nextView = null;
            }
            if (previewVersion != previewVersionRef.current) {
                disposeThree(nextView);
                return;
            }
            if (nextView) {
                viewRef.current = nextView;
                rebuildThreeRef.current = false;
                updateBaselineStyle(createBaselineStyleFromThreeView(nextView, baselineMmRef.current));
                if (threeRef.current) {
                    threeRef.current.style.display = "block";
                    threeRef.current.style.visibility = "visible";
                }
                if (canvasRef.current) canvasRef.current.style.display = "none";
                if (oldView && oldView !== nextView) disposeThree(oldView);
                return;
            }
            if (threeRef.current) {
                threeRef.current.style.visibility = "visible";
                threeRef.current.style.display = "none";
            }
            if (oldView) disposeThree(oldView);
            viewRef.current = null;
            showFallbackPreview(stitches);
        });
    };

    // Render lại preview từ stitch đã cache; dùng chung cho lần load đầu, đổi màu và đổi kích thước container.
    const renderLatestPreview = () => {
        const stitches = stitchesRef.current || [];
        if (!stitches.length) return;
        if (!useWebGL) {
            cancelPendingWebGLBuild();
            disposeThree(viewRef.current);
            viewRef.current = null;
            rebuildThreeRef.current = false;
            showFallbackPreview(stitches);
            return;
        }
        if (rebuildThreeRef.current) {
            showFallbackPreview(stitches);
            scheduleWebGLBuild(stitches, previewVersionRef.current);
            return;
        }
        if (viewRef.current && !rebuildThreeRef.current) {
            updateThreeColor(viewRef.current, threadColorRef.current);
            if (resizeThreeView(viewRef.current, threeRef.current)) {
                updateBaselineStyle(createBaselineStyleFromThreeView(viewRef.current, baselineMmRef.current));
                if (threeRef.current) {
                    threeRef.current.style.display = "block";
                    threeRef.current.style.visibility = "visible";
                }
                if (canvasRef.current) canvasRef.current.style.display = "none";
                return;
            }
            disposeThree(viewRef.current);
            viewRef.current = null;
        }
        const oldView = viewRef.current;
        const nextView = renderThree(threeRef.current, stitches, threadColorRef.current);
        if (nextView) {
            viewRef.current = nextView;
            rebuildThreeRef.current = false;
            updateBaselineStyle(createBaselineStyleFromThreeView(nextView, baselineMmRef.current));
            if (threeRef.current) {
                threeRef.current.style.display = "block";
                threeRef.current.style.visibility = "visible";
            }
            if (oldView && oldView !== nextView) disposeThree(oldView);
        } else if (oldView) {
            viewRef.current = oldView;
            if (threeRef.current) {
                threeRef.current.style.display = "block";
                threeRef.current.style.visibility = "visible";
            }
        } else {
            viewRef.current = null;
        }
        if (viewRef.current) {
            updateBaselineStyle(createBaselineStyleFromThreeView(viewRef.current, baselineMmRef.current));
            if (threeRef.current) {
                threeRef.current.style.display = "block";
                threeRef.current.style.visibility = "visible";
            }
            if (canvasRef.current) canvasRef.current.style.display = "none";
        } else {
            showFallbackPreview(stitches);
        }
    };

    // Đợi browser apply xong posWidth/posHeight trước khi fit 3D, tránh lần đầu compose bị render theo size cũ.
    const scheduleRenderLatestPreview = () => {
        if (renderRafRef.current) cancelAnimationFrame(renderRafRef.current);
        renderRafRef.current = requestAnimationFrame(() => {
            renderRafRef.current = requestAnimationFrame(() => {
                renderRafRef.current = null;
                renderLatestPreview();
            });
        });
    };

    useEffect(() => {
        threadColorRef.current = threadColor;
        scheduleRenderLatestPreview();
    }, [threadColor]);

    useEffect(() => {
        baselineMmRef.current = baselineMm;
        scheduleRenderLatestPreview();
    }, [baselineMm, showBaseline]);

    useEffect(() => {
        const target = threeRef.current ? threeRef.current.parentElement || threeRef.current : null;
        if (!target || typeof ResizeObserver == "undefined") return undefined;
        const observer = new ResizeObserver(() => scheduleRenderLatestPreview());
        observer.observe(target);
        return () => {
            observer.disconnect();
            if (renderRafRef.current) cancelAnimationFrame(renderRafRef.current);
            cancelPendingWebGLBuild();
        };
    }, []);

    useEffect(() => {
        return () => {
            if (renderRafRef.current) cancelAnimationFrame(renderRafRef.current);
            cancelPendingWebGLBuild();
            disposeThree(viewRef.current);
            viewRef.current = null;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        if (!dstUrl) return undefined;
        setStatus("loading");
        fetch(dstUrl)
            .then((res) => {
                if (!res.ok) throw new Error("Cannot fetch DST file.");
                return res.arrayBuffer();
            })
            .then((buffer) => {
                if (cancelled || !buffer) return;
                const stitches = parseDst(buffer);
                stitchesRef.current = stitches;
                previewVersionRef.current += 1;
                rebuildThreeRef.current = true;
                setStatus(stitches.length > 0 ? "" : "empty");
                showFallbackPreview(stitches);
                if (!useWebGL) {
                    scheduleRenderLatestPreview();
                    return null;
                }
                return ensureThree()
                    .then(() => {
                        if (cancelled) return;
                        scheduleRenderLatestPreview();
                    })
                    .catch(() => {
                        if (!cancelled) showFallbackPreview(stitches);
                    });
            })
            .catch(() => {
                if (!cancelled) {
                    setStatus("error");
                    setBaselineStyle(null);
                }
            });
        return () => {
            cancelled = true;
            if (renderRafRef.current) cancelAnimationFrame(renderRafRef.current);
            cancelPendingWebGLBuild();
        };
    }, [dstUrl, useWebGL]);

    return (
        <div className={`wh-100 noselect disable-double-tap embroidery-dst-canvas-3d ${className || ""}`} style={{ position: "relative", width: "100%", height: "100%", pointerEvents: "none", ...style }}>
            <div ref={threeRef} style={{ position: "absolute", inset: 0 }} />
            <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, display: "block", width: "100%", height: "100%" }} />
            {baselineStyle && <span style={{ position: "absolute", height: 3, background: "#f20e45", borderRadius: 999, boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.65)", transform: "translateY(-50%)", ...baselineStyle }} />}
            {status && status != "loading" && (
                <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#f20e45", fontSize: 10, fontWeight: 600 }}>{status == "error" ? "DST lỗi" : "DST trống"}</span>
            )}
        </div>
    );
}

export default memo(EmbroideryDstCanvas3D);
